import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import {
  ADULT_AUTH_UNAVAILABLE,
  accountsEnabled,
  requestWithAdultRefresh,
  restoreAdultSession,
} from './AdultAuthPanel';
import './AdultWorkspacePage.css';
import { SpaceIllustration } from './AccountsVisuals';
import StudentProfilesSection from './StudentProfilesSection';
import AdultMembersSection, { AcceptAdultInvitation } from './AdultMembersSection';

const WORKSPACES = {
  family: {
    role: 'GUARDIAN',
    title: 'FAMILIA',
    singular: 'Familia',
    listPath: '/families',
    fields: ['name', 'relationship_label'],
    createLabel: 'CREAR FAMILIA',
  },
  teacher: {
    role: 'TEACHER',
    title: 'MAESTRO',
    singular: 'Clase',
    listPath: '/classes',
    fields: ['name', 'school_year'],
    createLabel: 'CREAR CLASE',
  },
};

export function canOpenWorkspace(profile, workspace) {
  return Boolean(profile?.roles?.includes(WORKSPACES[workspace].role));
}

export function workspaceErrorMessage(error) {
  if (error?.status === 403 || error?.status === 404) {
    return 'Esta experiencia no está habilitada para tu cuenta.';
  }
  return 'No fue posible actualizar esta información. Inténtalo de nuevo.';
}

export function buildWorkspacePatch(workspace, draft) {
  const body = {};
  if (Object.prototype.hasOwnProperty.call(draft, 'name')) {
    body.name = String(draft.name ?? '').trim();
  }
  if (workspace === 'teacher' && Object.prototype.hasOwnProperty.call(draft, 'school_year')) {
    body.school_year = String(draft.school_year ?? '').trim() || null;
  }
  return body;
}

export function retainPendingEdits(currentDraft, submittedDraft) {
  const next = { ...currentDraft };
  Object.keys(submittedDraft).forEach((field) => {
    if (next[field] === submittedDraft[field]) delete next[field];
  });
  return next;
}

export default function AdultWorkspacePage({ workspace }) {
  const config = WORKSPACES[workspace];
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [state, setState] = useState('loading');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [revision, setRevision] = useState(0);
  const [memberPermissions, setMemberPermissions] = useState({});
  const applyMemberPermissions = useCallback((id, allowed) => {
    setMemberPermissions((current) => ({ ...current, [id]: allowed }));
  }, []);

  const itemId = workspace === 'family' ? 'family_id' : 'class_id';

  useEffect(() => {
    if (!accountsEnabled) return undefined;

    let active = true;

    async function restore() {
      const adult = await restoreAdultSession();
      if (!active) return;
      if (adult === ADULT_AUTH_UNAVAILABLE) {
        setState('unavailable');
        return;
      }
      if (!adult) {
        setState('signed-out');
        return;
      }
      setProfile(adult);
      if (!canOpenWorkspace(adult, workspace)) {
        setState('forbidden');
        return;
      }

      try {
        const records = await requestWithAdultRefresh(config.listPath);
        if (!active) return;
        setItems(records);
        setState('ready');
      } catch (error) {
        if (!active) return;
        setState(error?.status === 401 ? 'signed-out' : 'error');
        setMessage(workspaceErrorMessage(error));
      }
    }

    restore();
    return () => {
      active = false;
    };
  }, [workspace, config.listPath, revision]);

  const activeItems = useMemo(
    () => (workspace === 'teacher' ? items.filter((item) => item.status === 'ACTIVE') : items),
    [items, workspace],
  );

  if (!accountsEnabled) return <Navigate to="/" replace />;

  async function createItem(event) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const body = { name: String(form.get('name') || '').trim() };

    if (workspace === 'family') {
      body.relationship_label = String(form.get('relationship_label') || 'TUTOR');
    } else {
      const schoolYear = String(form.get('school_year') || '').trim();
      if (schoolYear) body.school_year = schoolYear;
    }

    setBusy(true);
    setMessage('');
    try {
      const created = await requestWithAdultRefresh(config.listPath, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setItems((current) => [...current, created]);
      formElement.reset();
    } catch (error) {
      setMessage(workspaceErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function saveItem(item) {
    const id = item[itemId];
    const draft = drafts[id] || {};
    const body = buildWorkspacePatch(workspace, draft);
    if (Object.keys(body).length === 0) return;

    setBusy(true);
    setMessage('');
    try {
      const updated = await requestWithAdultRefresh(`${config.listPath}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setItems((current) => current.map((record) => (record[itemId] === id ? updated : record)));
      setDrafts((current) => {
        const next = { ...current };
        const remaining = retainPendingEdits(current[id], draft);
        if (Object.keys(remaining).length) next[id] = remaining;
        else delete next[id];
        return next;
      });
    } catch (error) {
      setMessage(workspaceErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function archiveItem(item) {
    if (!window.confirm(`¿Archivar ${item.name}?`)) return;
    setBusy(true);
    setMessage('');
    try {
      const updated = await requestWithAdultRefresh(`${config.listPath}/${item[itemId]}/archive`, { method: 'POST' });
      setItems((current) => current.map((record) => (record[itemId] === item[itemId] ? updated : record)));
    } catch (error) {
      setMessage(workspaceErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="tmag-adult-workspace" aria-live="polite">
      <Link className="tmag-adult-workspace__back" to="/account">← MI CUENTA</Link>
      {' '}<Link className="tmag-adult-workspace__back" to="/">VOLVER A JUGAR GRATIS</Link>
      <header className="tmag-workspace-hero"><div><p className="tmag-workspace-eyebrow">TU TRIPULACIÓN TECHNOMATHPR</p><h1>{workspace === 'family' ? 'Tu familia, una gran aventura' : 'Tu clase está lista para explorar'}</h1><p>{workspace === 'family' ? 'Acompaña a tus pequeños exploradores, un descubrimiento a la vez.' : 'Agrega estudiantes y prepara sus accesos. TechnoMathPR se encarga del resto.'}</p></div><SpaceIllustration compact /></header>

      {state === 'loading' && <p>Verificando sesión adulta…</p>}
      {state === 'unavailable' && <p>El servicio de cuentas no está disponible temporalmente.</p>}
      {state === 'signed-out' && <p>Inicia sesión como adulto para continuar.</p>}
      {state === 'forbidden' && <p>Esta experiencia no está habilitada para tu cuenta.</p>}
      {state === 'error' && <p>{message}</p>}

      {state === 'ready' && (
        <>
          <p className="tmag-adult-workspace__identity">{profile?.email}</p>
          <details className="tmag-workspace-secondary"><summary>¿Recibiste una invitación?</summary><AcceptAdultInvitation workspace={workspace} onAccepted={() => setRevision((value) => value + 1)} /></details>
          <details className="tmag-workspace-create" open={activeItems.length === 0}><summary>{workspace === 'family' ? 'Crear familia' : 'Crear clase'}</summary>
          <form className="tmag-adult-workspace__create" onSubmit={createItem}>
            <label>
              Nombre
              <input name="name" maxLength="160" required />
            </label>
            {workspace === 'family' ? (
              <label>
                Relación
                <select name="relationship_label" defaultValue="TUTOR">
                  <option value="PADRE">Padre</option>
                  <option value="MADRE">Madre</option>
                  <option value="TUTOR">Tutor</option>
                  <option value="OTRO">Otro</option>
                </select>
              </label>
            ) : (
              <label>
                Año escolar
                <input name="school_year" maxLength="32" placeholder="2026-2027" />
              </label>
            )}
            <button type="submit" disabled={busy}>{config.createLabel}</button>
          </form></details>

          {message && <p className="tmag-adult-workspace__message">{message}</p>}
          {activeItems.length === 0 ? (
            <p className="tmag-adult-workspace__empty">
              {workspace === 'family' ? 'Tu primera aventura en familia empieza aquí. Crea tu familia y agrega a tus niños.' : 'Prepara tu primera tripulación. Crea una clase y después agrega a tus estudiantes.'}
            </p>
          ) : (
            <section className="tmag-adult-workspace__list" aria-label={`${config.title} disponibles`}>
              {activeItems.map((item) => {
                const id = item[itemId];
                const draft = drafts[id] || {};
                return (
                  <article className="tmag-adult-workspace__card" key={id}>
                    <header className="tmag-workspace-card-heading"><span>{workspace === 'family' ? 'MI FAMILIA' : 'MI CLASE'}</span><h2>{item.name}</h2></header>
                    <StudentProfilesSection workspace={workspace} resourceId={id} classTitle={item.name} />
                    <details className="tmag-workspace-secondary"><summary>{workspace === 'family' ? 'Editar datos de familia' : 'Editar datos de clase'}</summary>
                    <label>
                      Nombre
                      <input
                        value={draft.name ?? item.name}
                        onChange={(event) => setDrafts((current) => ({
                          ...current,
                          [id]: { ...current[id], name: event.target.value },
                        }))}
                        maxLength="160"
                      />
                    </label>
                    {workspace === 'teacher' && (
                      <label>
                        Año escolar
                        <input
                          value={draft.school_year ?? item.school_year ?? ''}
                          onChange={(event) => setDrafts((current) => ({
                            ...current,
                            [id]: { ...current[id], school_year: event.target.value },
                          }))}
                          maxLength="32"
                        />
                      </label>
                    )}
                    <div className="tmag-adult-workspace__actions">
                      <button type="button" onClick={() => saveItem(item)} disabled={busy}>GUARDAR</button>
                      {workspace === 'teacher' && memberPermissions[id] === true && (
                        <button type="button" className="tmag-adult-workspace__archive" onClick={() => archiveItem(item)} disabled={busy}>
                          ARCHIVAR
                        </button>
                      )}
                    </div>
                    </details>
                    <details className="tmag-workspace-secondary"><summary>{workspace === 'family' ? 'Adultos e invitaciones' : 'Maestros e invitaciones'}</summary><AdultMembersSection workspace={workspace} resourceId={id} onPermissionChange={applyMemberPermissions} onMembershipEnded={() => setRevision((value) => value + 1)} /></details>
                  </article>
                );
              })}
            </section>
          )}
        </>
      )}
    </main>
  );
}
